import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withStyles, MuiThemeProvider, createTheme } from '@material-ui/core/styles';
import * as appPropTypes from '../../appPropTypes';
import { withRoomContext } from '../../../RoomContext';
import * as roomActions from '../../../actions/roomActions';
import * as settingsActions from '../../../actions/settingsActions';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Button from '@material-ui/core/Button';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';

const styles = (theme) =>
	({
		root :
		{
			zIndex : 99,
			width  : '100%',
			height : '100%'
		},
		dialogPaper :
		{
			padding                        : '1% 1%',
			width                          : '20vw',
			height                         : '30vw',
			[theme.breakpoints.down('lg')] :
			{
				width : '20vw'
			},
			[theme.breakpoints.down('md')] :
			{
				width : '50vw'
			},
			[theme.breakpoints.down('sm')] :
			{
				width : '70vw'
			},
			[theme.breakpoints.down('xs')] :
			{
				width : '90vw'
			},
			backgroundColor : '#666666'
		},
		lock :
		{
			padding : theme.spacing(2)
		},
		title :
		{
			width         : '100%',
			display       : 'flex',
			flexDirection : 'column',
			alignItems    : 'center'
		},
		titleIcon :
		{
			width     : '4rem',
			height    : '4rem',
			transform : 'scaleX(-1)'
		},
		titleText :
		{
			color    : 'white',
			fontSize : '0.8rem'
		},
		promoteBtn :
		{
			width           : '40%',
			border          : '1px solid #8eba14',
			backgroundColor : '#8eba14',
			color           : 'white'
		},
		cancelBtn :
		{
			width           : '40%',
			border          : '1px solid #fff',
			backgroundColor : 'transparent',
			color           : 'white'
		},
		timeout :
		{
			border          : '1px solid #8eba14',
			borderRadius    : '0.5rem',
			backgroundColor : '#8eba14',
			width           : '100%',
			textAlign       : 'center',
			fontSize        : '1.5rem',
			marginBottom    : '20%',
			padding         : '0.1rem 0'
		},
		serviceName :
		{
			border          : '1px solid #fff',
			borderRadius    : '0.5rem',
			backgroundColor : '#fff',
			width           : '100%',
			textAlign       : 'center',
			fontSize        : '0.9rem',
			marginBottom    : '10%',
			padding         : '0.4rem 0',
			color           : '#666666'
		},
		osInfo :
		{
			display        : 'flex',
			justifyContent : 'space-between',
			width          : '100%',
			borderBottom   : '1px solid #fff',
			fontSize       : '0.9rem',
			marginBottom   : '10%'
		},
		browserInfo :
		{
			display        : 'flex',
			justifyContent : 'space-between',
			width          : '100%',
			borderBottom   : '1px solid #fff',
			fontSize       : '0.9rem',
			marginBottom   : '10%'
		}
	});

const theme = createTheme({
	overrides : {
		MuiBackdrop : {
			root : {
				backgroundColor : 'transparent'
			}
		},
		MuiDialogTitle : {
			root : {
				backgroundColor : 'transparent',
				color           : '#9ec317',
				width           : '100%'
			}
		},
		MuiDialogContent : {
			root : {
				color          : 'white',
				justifyContent : 'center',
				display        : 'flex',
				flexDirection  : 'column',
				alignItems     : 'center'
			}
		},
		MuiDialogActions : {
			root : {
				justifyContent : 'space-around'
			}
		},
		MuiTypography : {
			colorTextSecondary : {
				color : 'white'
			}
		},
		MuiPaper : {
			rounded : {
				borderRadius : '0.6rem'
			}
		}
	}
});

const OnInviteDialog = ({
	classes,
	roomClient,
	room,
	rooms,
	invitedRoomId,
	mediaPerms,
	setAudioMuted,
	setVideoMuted,
	handleCloseOnInviteDialog
}) =>
{
	const [ userName, setUserName ] = useState('');

	useEffect(() =>
	{
		const targetRoom = rooms.find((item) => item.id === invitedRoomId);

		setUserName(targetRoom.user);
	}, [ rooms, invitedRoomId ]);

	const joinRoom = useCallback(() =>
	{
		setAudioMuted(false);
		setVideoMuted(false);

		const parts = window.location.hostname.split('.');
		const subdomain = parts.shift();
		const encodedRoomId = encodeURIComponent(invitedRoomId);

		roomClient.forceClose();

		roomClient.join({
			roomId    : encodedRoomId,
			joinVideo : mediaPerms.video,
			joinAudio : mediaPerms.audio,
			subdomain : subdomain
		});
	}, [
		roomClient,
		mediaPerms,
		invitedRoomId,
		setAudioMuted,
		setVideoMuted
	]);

	return (
		<MuiThemeProvider theme={theme}>
			<Dialog
				className={classes.root}
				open={room.inviteDialogOpen}
				onClose={() => {}}
				classes={{
					paper : classes.dialogPaper
				}}
			>
				<DialogTitle id='form-dialog-title'>
					<div className={classes.title}>
						<PhoneInTalkIcon className={classes.titleIcon}/>
						<div className={classes.titleText}>招待</div>
					</div>
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						以下の利用者から招待が来ています。
					</DialogContentText>
					<div className={classes.serviceName}>
						{userName}
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						className={classes.cancelBtn}
						onClick={handleCloseOnInviteDialog}
						color='primary'
					>
						<FormattedMessage
							id='label.close'
							defaultMessage='Close'
						/>
					</Button>
					<Button
						className={classes.promoteBtn}
						onClick={joinRoom}
						color='primary'
					>
						<FormattedMessage
							id='label.join'
							defaultMessage='Join'
						/>
					</Button>
				</DialogActions>
			</Dialog>
		</MuiThemeProvider>
	);
};

OnInviteDialog.propTypes =
{
	classes                   : PropTypes.object.isRequired,
	roomClient                : PropTypes.object.isRequired,
	room                      : appPropTypes.Room.isRequired,
	rooms                     : PropTypes.array.isRequired,
	invitedRoomId             : PropTypes.string.isRequired,
	mediaPerms                : PropTypes.object.isRequired,
	setAudioMuted             : PropTypes.func.isRequired,
	setVideoMuted             : PropTypes.func.isRequired,
	handleCloseOnInviteDialog : PropTypes.func.isRequired
};

const makeMapStateToProps = () =>
{
	const mapStateToProps = (state) =>
	{
		return {
			room          : state.room,
			rooms         : state.me.rooms,
			mediaPerms    : state.settings.mediaPerms,
			invitedRoomId : state.room.invitedRoomId
		};
	};

	return mapStateToProps;
};

const mapDispatchToProps = (dispatch) =>
{
	return {
		setAudioMuted : (flag) =>
		{
			dispatch(settingsActions.setAudioMuted(flag));
		},
		setVideoMuted : (flag) =>
		{
			dispatch(settingsActions.setVideoMuted(flag));
		},
		handleCloseOnInviteDialog : () =>
		{
			dispatch(roomActions.setInviteDialogOpen(false, ''));
		}
	};
};

export default withRoomContext(connect(
	makeMapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.room === next.room &&
				prev.me.rooms === next.me.rooms &&
				prev.me.invitedRoomId === next.me.invitedRoomId
			);
		}
	}
)(withStyles(styles)(OnInviteDialog)));